import type { FC } from "react";

import {
  Button,
  TableContainer as MuiTableContainer,
  Typography,
  TypographyProps,
} from "@mui/material";
import styled from "@mui/material/styles/styled";

import TableRow from "@app/analytics/components/Table/TableRow";

import { Row, StackProps } from "@rui/Stack";

import type { CustomVocabularyTypeConfig } from "./constants";

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  padding: theme.spacing(1),
  ".MuiTableBody-root & .MuiTableCell-root": {
    fontFamily: theme.typography.fontFamilySecondary,
    fontSize: "0.75rem",
    color: theme.palette.shades.neutral[600],
    padding: "8px",
    height: "40px",
  },
}));

export const TableContainer = styled(MuiTableContainer)(({ theme }) => ({
  maxHeight: "210px",
  borderRadius: "8px",
  border: `1px solid ${theme.palette.shades.neutral[100]}`,
  "& th": {
    backgroundColor: "#FFF",
  },
}));

export const CustomVocabularyChip = styled<FC<TypographyProps>>((props) => (
  <Typography variant="bodySecondaryMed" {...props} />
))(({ theme }) => ({
  color: theme.palette.shades.neutral[700],
  backgroundColor: theme.palette.shades.neutral[50],
  fontFamily: theme.typography.fontFamilySecondary,
  width: "fit-content",
  borderRadius: "2px",
  padding: theme.spacing(0.5, 1),
}));

export const TypeSelectorButton = styled(Button)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilySecondary,
  color: theme.palette.shades.neutral[500],
  fontSize: "0.75rem",
  "& .MuiButton-startIcon": {
    marginRight: theme.spacing(0.5),
  },
  "& .MuiSvgIcon-root": {
    fontSize: "14px",
    color: theme.palette.shades.neutral[300],
  },
  "&.Mui-disabled": {
    color: theme.palette.shades.neutral[500],
  },
}));

type AddVocabularyInputRowProps = StackProps & { error: boolean };

export const AddVocabularyInputRow = styled<FC<AddVocabularyInputRowProps>>(
  ({ error: _e, ...props }) => <Row variant="left" {...props} />
)(({ error, theme }) => ({
  width: "100%",
  border: `1px solid ${
    error ? theme.palette.error.main : theme.palette.shades.neutral[100]
  }`,
  borderRadius: "4px",
  padding: theme.spacing(1),
  minHeight: "40px",
  "&:focus-within": {
    borderColor: error
      ? theme.palette.error.main
      : theme.palette.shades.primary[500],
  },
  "& .MuiInputBase-input": {
    fontWeight: 500,
    padding: 0,
    fontFamily: theme.typography.fontFamilySecondary,
    fontSize: "0.75rem",
    color: theme.palette.shades.neutral[700],
    "&::placeholder": {
      color: theme.palette.shades.neutral[400],
      opacity: 1,
    },
  },
  "& .MuiSelect-select": {
    // all these styles have to be !important because the Dropdown applies conflicting
    // styles that will override these otherwise
    fontFamily: `${theme.typography.fontFamilySecondary} !important`,
    fontSize: "0.75rem !important",
    color: `${theme.palette.shades.neutral[400]} !important`,
  },
}));

export const TypeSelectorLabel = ({
  config,
  selected,
}: {
  config: CustomVocabularyTypeConfig;
  selected: boolean;
}) => {
  const { Icon, label } = config;
  return (
    <Row
      variant="left"
      spacing={1}
      sx={{ color: selected ? "shades.neutral.800" : "shades.neutral.600" }}
    >
      <Icon
        sx={{
          fontSize: "14px",
          color: selected ? "shades.neutral.500" : "shades.neutral.300",
        }}
      />
      <Typography variant="bodySecondaryReg">{label}</Typography>
    </Row>
  );
};
